import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Permission } from '../enums/permission.enum';
import { PermissionService } from '../services/permission.service';

export interface PermissionGuardData {
  permissions: Permission[];
  redirectTo?: string;
}

/**
 * A functional route guard that checks for user permissions before activating a route.
 *
 * It reads permission requirements from the route's `data` property.
 * If the user lacks the necessary permissions, they are redirected to a specified path or a default forbidden page.
 *
 * @remarks
 * The route's `data` property must be configured with a `PermissionGuardData` object, which can contain:
 * - `permissions`: An array of permission strings to check. If empty or not provided, the guard allows access.
 * - `redirectTo`: (Optional) The path to redirect to if the permission check fails. Defaults to '/forbidden'.
 *
 * @example
 * ```typescript
 * // Example usage in a routing module:
 * const routes: Routes = [
 *   {
 *     path: 'admin-dashboard',
 *     component: AdminDashboardComponent,
 *     canActivate: [permissionGuard],
 *     data: {
 *       // Requires the user to have both 'Permission.WebinarCreate' AND 'Permission.WebinarEdit' permissions.
 *       permissions: ['Permission.WebinarCreate', 'Permission.WebinarEdit'],
 *       requireAll: true,
 *       redirectTo: '/forbidden' // Optional: custom redirect path
 *     }
 *   },
 *   {
 *     path: 'editor-panel',
 *     component: EditorPanelComponent,
 *     canActivate: [permissionGuard],
 *     data: {
 *       // Requires the user to have either 'Permission.ClientSettings' OR 'Permission.UserManage' permission.
 *       permissions: ['Permission.ClientSettings', 'Permission.UserManage']
 *     }
 *   }
 * ];
 * ```
 */
export const permissionGuard: CanActivateFn = (route, _state) => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const data = route.data as PermissionGuardData;

  if (!data.permissions || data.permissions.length === 0) {
    return true;
  }

  if (!permissionService.canPerformAny(data.permissions)) {
    const redirectTo = data.redirectTo || '/forbidden';
    router.navigate([redirectTo]);
    return false;
  }

  return true;
};
